/**
 * MKS-327 — the two questions `/time` used to answer by pattern-matching a
 * NAME, pulled out of `TimeEntryPage.tsx` so they can be tested without
 * rendering a 1100-line page.
 */
import type { TimeOff } from '../services/api/timeOff';

/** Just enough of the signed-in user's employee type to answer the question. */
export interface EmployeeTypeLike {
  name?: string | null;
  category?: string | null;
}

/**
 * Is this person a contractor? Their time off is unpaid, so it contributes no
 * hours to the month total.
 *
 * `employeeType.category` (`'employee' | 'contractor'`) is the one
 * authoritative flag, and the same one the backend keys off
 * (`employeeTypeCategory.ts`). The old rule was
 * `name.toLowerCase().includes('freelance')`, which quietly answered NO for
 * anyone on a type named "IČO", "DPP" or "Contractor", and quietly answered
 * YES for an employee type someone named "Freelance-adjacent".
 *
 * The name rule survives only as the fallback for a cached `/auth/me` from
 * before the category was surfaced — hence the `category` presence check.
 */
export function isContractorEmployeeType(
  employeeType: EmployeeTypeLike | null | undefined
): boolean {
  if (employeeType?.category) {
    return employeeType.category === 'contractor';
  }
  return employeeType?.name?.toLowerCase().includes('freelance') ?? false;
}

/**
 * How many hours does one day of this time-off contribute to the month total
 * the `/time` grid compares against `working days × 8`?
 *
 * Zero when either:
 *  - the person is a contractor — their time off is unpaid; or
 *  - MKS-327, the TYPE does not count toward the hours target. That covers
 *    every PRESENCE type (home office, business trip, conference: the person
 *    logs their real entries that day, so adding time-off hours on top would
 *    book the day twice) and any absence type an admin marked as not reducing
 *    the target, such as unpaid leave.
 *
 * The row still appears in the grid at 0 h — knowing somebody was at a
 * conference is the point; it just does not fill their target.
 */
export function timeOffHoursTowardTarget(
  hours: number,
  timeOffType: TimeOff['timeOffType'],
  options: { isContractor: boolean }
): number {
  if (options.isContractor) return 0;
  if (timeOffType?.category === 'presence') return 0;
  if (timeOffType?.countsTowardHoursTarget === false) return 0;
  return hours;
}
